/**
 * flyerGeneration.js — Flyer orchestration helpers (Bannerbear-based flow)
 *
 * Builds the structured client-context payload the backend uses to:
 *   1. Run Gemini for copy / layout planning
 *   2. Pass the plan to Bannerbear for rendering
 *   3. Upload the result to S3 and return the URL
 *
 * The frontend is intentionally unaware of Bannerbear template IDs, Gemini
 * prompt structures, and whether rendering is sync or async.
 */

// ─── Low-level helpers ────────────────────────────────────────────────────────

/**
 * Returns true when value is a non-empty string.
 */
export function hasText(value) {
  return typeof value === 'string' && value.trim().length > 0;
}

/**
 * Returns trimmed string or empty string.
 */
export function clean(value) {
  return hasText(value) ? value.trim() : '';
}

/**
 * Returns trimmed string or null. Normalises empty strings to null.
 */
function nullify(value) {
  return hasText(value) ? value.trim() : null;
}

/**
 * Safely reads a key from the mergeFields map.
 * @param {Object} mergeFields  e.g. { '~Phone': '...', '~Website': '...' }
 * @param {string} key          e.g. '~Phone'
 * @returns {string}
 */
export function getMergeField(mergeFields, key) {
  if (!mergeFields || typeof mergeFields !== 'object') return '';
  return clean(mergeFields[key] ?? '');
}

// ─── Default stock images ─────────────────────────────────────────────────────
// Used as fallback reference images when the client has no images in their pool.
// One is picked at random per generation call so successive calls look varied.

const DEFAULT_STOCK_IMAGES = [
  'https://stock-images-prod.s3.us-east-1.amazonaws.com/4fcda9a34baab07016fdf23a62354282f1f6bede8e651757c1da3997eae1c894/image_1771431918993.png',
  'https://stock-images-prod.s3.us-east-1.amazonaws.com/4fcda9a34baab07016fdf23a62354282f1f6bede8e651757c1da3997eae1c894/image_1771431825313.png',
  'https://adlersuite-assets-prod.s3.amazonaws.com/70c4908f-a367-4059-ac50-a27b99ef7aac.jpg'
];

function pickDefaultStockImage() {
  return DEFAULT_STOCK_IMAGES[
    Math.floor(Math.random() * DEFAULT_STOCK_IMAGES.length)
  ];
}

// ─── Payload builder ──────────────────────────────────────────────────────────

/**
 * Builds the multi-template production payload for the Bannerbear flyer endpoint.
 *
 * All user-facing copy is passed through flyerContent. clientContext carries only
 * asset references (image, logo). generationContext tracks regeneration attempts
 * so the backend can rotate templates on re-generates.
 *
 * @param {{
 *   clientContext: {
 *     clientId: string,
 *     brand?: { primaryColor?: string, secondaryColor?: string, logo?: string },
 *     logoUrl?: string | null
 *   },
 *   formInput: {
 *     title: string,
 *     mainFlyerText: string,
 *     details?: string,
 *     phone: string,
 *     contactUsText: string,
 *     website: string
 *   },
 *   imagePool?: Array<{ url: string }>,
 *   selectedImageUrl?: string | null,
 *   dryRun?: boolean,
 *   regenerationCount?: number
 * }} param
 *
 * @returns {{
 *   clientId: string,
 *   flyerContent: {
 *     title: string | null,
 *     mainFlyerText: string | null,
 *     details: string | null,
 *     phone: string | null,
 *     contactUsText: string | null,
 *     website: string | null
 *   },
 *   clientContext: { referenceImageUrl: string | null, logoUrl: string | null },
 *   renderOptions: { aspectRatio: string, size: number },
 *   debugOptions: { dryRun: boolean },
 *   generationContext: { regenerationCount: number }
 * }}
 */
export function buildBannerbearFlyerRequest({
  clientContext,
  formInput,
  imagePool = [],
  selectedImageUrl = null,
  dryRun = false,
  regenerationCount = 0
}) {
  const { clientId, brand, logoUrl: contextLogoUrl = null } = clientContext;

  const {
    title,
    mainFlyerText,
    details = '',
    phone = '',
    contactUsText = '',
    website = ''
  } = formInput;

  const normalizedMainFlyerText = nullify(mainFlyerText);
  const normalizedDetails = nullify(details);

  // Logo: forward-compatibility — backend may use in later versions
  const resolvedLogoUrl =
    nullify(contextLogoUrl) || nullify(brand?.logo) || null;

  // Reference image: UI selection > first valid pool image > random default stock image
  let referenceImageUrl = null;
  if (nullify(selectedImageUrl)) {
    referenceImageUrl = selectedImageUrl.trim();
  } else {
    const first = imagePool.find((img) => hasText(img?.url));
    referenceImageUrl = first ? first.url.trim() : pickDefaultStockImage();
  }

  return {
    clientId: clean(clientId),
    flyerContent: {
      title: nullify(title),
      mainFlyerText: normalizedMainFlyerText,
      // Keep legacy aliases while backend contracts converge.
      details: normalizedDetails,
      detail: normalizedDetails,
      description: normalizedMainFlyerText,
      phone: nullify(phone),
      contactUsText: nullify(contactUsText),
      website: nullify(website)
    },
    clientContext: {
      referenceImageUrl,
      logoUrl: resolvedLogoUrl
    },
    renderOptions: {
      aspectRatio: '1:1',
      size: 1080
    },
    debugOptions: {
      dryRun: !!dryRun
    },
    generationContext: {
      regenerationCount:
        typeof regenerationCount === 'number' ? regenerationCount : 0
    }
  };
}

// ─── AI generate+render request builder ─────────────────────────────────────

/**
 * Builds the payload for the AI generate+render flow (action: "generate_ai_flyer").
 * Gemini generates copy AND Bannerbear renders in a single backend call.
 *
 * @param {{
 *   clientId: string,
 *   aiPrompt: string,
 *   clientContext: {
 *     clientName?: string | null,
 *     phone?: string | null,
 *     website?: string | null,
 *     logoUrl?: string | null
 *   },
 *   imagePool?: Array<{ url: string }>,
 *   selectedImageUrl?: string | null,
 *   aiGenerationCount?: number,
 *   dryRun?: boolean
 * }} param
 */
export function buildAiFlyerRequest({
  clientId,
  aiPrompt,
  clientContext = {},
  imagePool = [],
  selectedImageUrl = null,
  aiGenerationCount = 0,
  dryRun = false,
  brandOptions = {}
}) {
  const {
    clientName = null,
    phone = null,
    website = null,
    logoUrl: contextLogoUrl = null
  } = clientContext;

  // referenceImageUrl: UI selection > first valid pool image > default stock image
  let referenceImageUrl = null;
  if (nullify(selectedImageUrl)) {
    referenceImageUrl = selectedImageUrl.trim();
  } else {
    const first = imagePool.find((img) => hasText(img?.url));
    referenceImageUrl = first ? first.url.trim() : pickDefaultStockImage();
  }

  return {
    action: 'generate_ai_flyer',
    clientId: clean(clientId),
    aiPrompt: clean(aiPrompt),
    clientContext: {
      clientName: nullify(clientName),
      phone: nullify(phone),
      website: nullify(website),
      referenceImageUrl,
      logoUrl: nullify(contextLogoUrl)
    },
    brandOptions: {
      useLogo: !!brandOptions.useLogo,
      logoUrl: nullify(brandOptions.logoUrl),
      usePrimaryColor: !!brandOptions.usePrimaryColor,
      primaryColor: nullify(brandOptions.primaryColor),
      useSecondaryColor: !!brandOptions.useSecondaryColor,
      secondaryColor: nullify(brandOptions.secondaryColor)
    },
    renderOptions: {
      aspectRatio: '1:1',
      size: 1080
    },
    generationContext: {
      regenerationCount:
        typeof aiGenerationCount === 'number' ? aiGenerationCount : 0
    },
    debugOptions: {
      dryRun: !!dryRun
    }
  };
}

// ─── Manual render request builder ───────────────────────────────────────────

/**
 * Builds the payload for the manual render flow (action: "render_flyer").
 * Bannerbear renders using the provided flyerContent; Gemini is NOT called.
 *
 * @param {{
 *   clientId: string,
 *   flyerContent: {
 *     title: string,
 *     mainFlyerText: string,
 *     details?: string,
 *     phone: string,
 *     contactUsText: string,
 *     website: string
 *   },
 *   clientContext: { logoUrl?: string | null },
 *   imagePool?: Array<{ url: string }>,
 *   selectedImageUrl?: string | null,
 *   manualRenderCount?: number,
 *   dryRun?: boolean
 * }} param
 */
export function buildManualRenderRequest({
  clientId,
  flyerContent,
  clientContext = {},
  imagePool = [],
  selectedImageUrl = null,
  manualRenderCount = 0,
  dryRun = false,
  brandOptions = {}
}) {
  const { logoUrl: contextLogoUrl = null } = clientContext;

  const {
    title,
    mainFlyerText,
    details = '',
    phone = '',
    contactUsText = '',
    website = ''
  } = flyerContent;

  const normalizedMainFlyerText = nullify(mainFlyerText);
  const normalizedDetails = nullify(details);

  const resolvedLogoUrl = nullify(contextLogoUrl);

  // referenceImageUrl: UI selection > first valid pool image > default stock image
  let referenceImageUrl = null;
  if (nullify(selectedImageUrl)) {
    referenceImageUrl = selectedImageUrl.trim();
  } else {
    const first = imagePool.find((img) => hasText(img?.url));
    referenceImageUrl = first ? first.url.trim() : pickDefaultStockImage();
  }

  return {
    action: 'render_flyer',
    clientId: clean(clientId),
    flyerContent: {
      title: nullify(title),
      mainFlyerText: normalizedMainFlyerText,
      // Keep legacy aliases while backend contracts converge.
      details: normalizedDetails,
      detail: normalizedDetails,
      description: normalizedMainFlyerText,
      phone: nullify(phone),
      contactUsText: nullify(contactUsText),
      website: nullify(website)
    },
    clientContext: {
      referenceImageUrl,
      logoUrl: resolvedLogoUrl
    },
    brandOptions: {
      useLogo: !!brandOptions.useLogo,
      logoUrl: nullify(brandOptions.logoUrl),
      usePrimaryColor: !!brandOptions.usePrimaryColor,
      primaryColor: nullify(brandOptions.primaryColor),
      useSecondaryColor: !!brandOptions.useSecondaryColor,
      secondaryColor: nullify(brandOptions.secondaryColor)
    },
    renderOptions: {
      aspectRatio: '1:1',
      size: 1080
    },
    generationContext: {
      regenerationCount:
        typeof manualRenderCount === 'number' ? manualRenderCount : 0
    },
    debugOptions: {
      dryRun: !!dryRun
    }
  };
}
